import Question from "./components/Question"
import Results from "./components/Results"
import SolutionNav from "./components/SolutionNav"
import { SOLUTION_FINDER_CONFIG } from "./constants"
import type { Answer, Question as QuestionType, SolutionItem } from "./types"
import { applyBundleSubstitutions, calculateTotalQuestions } from "./utils"
import Layout from "../components/Layout"
import { SOLUTION_ITEMS } from "../constants"

import { trackSegmentEvent } from "@utils/analytics"
import React, { useState } from "react"

type Props = {
  variant?: "default" | "inStore"
}

const SolutionFinder = ({ variant = "default", ...props }: Props) => {
  const [solution, setSolution] = useState<SolutionItem[]>([])
  const [currentStep, setCurrentStep] = useState(1)
  const [totalQuestions, setTotalQuestions] = useState(
    calculateTotalQuestions(SOLUTION_FINDER_CONFIG, 1)
  )
  const [activeQuestion, setActiveQuestion] = useState<QuestionType>(
    SOLUTION_FINDER_CONFIG
  )
  const [questionHistory, setQuestionHistory] = useState<QuestionType[]>([
    SOLUTION_FINDER_CONFIG,
  ])
  const [solutionHistory, setSolutionHistory] = useState<SolutionItem[][]>([])
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([])
  const [multiselectedAnswers, setMultiselectedAnswers] = useState<Answer[]>([])
  const [resultDescription, setResultDescription] = useState<string>("")

  const progress = ((currentStep - 1) / totalQuestions) * 100

  const isInStoreVariant = variant === "inStore"

  const solutionWithoutExcludedItems = isInStoreVariant
    ? solution.filter((item) => item.value !== SOLUTION_ITEMS.tap_to_pay.value)
    : solution

  const displaySolution = isInStoreVariant
    ? solutionWithoutExcludedItems
    : applyBundleSubstitutions(solutionWithoutExcludedItems)

  const handleAnswer = (answers: Answer[]) => {
    const nextStep = currentStep + 1
    const nextQuestion = answers.find(
      (answer) => answer.nextQuestion
    )?.nextQuestion

    const newResultDescription = answers
      .map((answer) => answer.resultDescription)
      .filter((desc) => !!desc)
      .join(" ")

    if (newResultDescription) {
      setResultDescription(newResultDescription)
    }

    const solutionAdditions = answers.flatMap(
      (answer) => answer.solutionAdditions || []
    )

    const stepBreadcrumb = answers
      .map((answer) => answer.breadcrumb || answer.answer)
      .join(" + ")

    setBreadcrumbs((prev) => [...prev, stepBreadcrumb])
    trackSegmentEvent("Product Finder Answered Question", {
      journey: [...breadcrumbs, stepBreadcrumb],
    })

    if (nextQuestion) {
      setQuestionHistory((prev) => [...prev, nextQuestion])
      setActiveQuestion(nextQuestion)
      setTotalQuestions(calculateTotalQuestions(nextQuestion, nextStep))
    } else {
      trackSegmentEvent("Product Finder Reached Results")
      setTotalQuestions(calculateTotalQuestions(activeQuestion, currentStep))
    }

    setSolution((prev) => [
      ...prev,
      ...solutionAdditions.map((addition) => addition),
    ])
    setSolutionHistory((prev) => [...prev, solutionAdditions])

    setMultiselectedAnswers([])

    setCurrentStep(nextStep)
  }

  const toggleMultiselectAnswer = (answer: Answer) => {
    if (multiselectedAnswers.some((a) => a.answer === answer.answer)) {
      setMultiselectedAnswers((prev) =>
        prev.filter((a) => a.answer !== answer.answer)
      )
    } else {
      setMultiselectedAnswers((prev) => [...prev, answer])
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      const previousStep = currentStep - 1
      const newBreadcrumbs = breadcrumbs.slice(0, -1)
      let previousQuestion = activeQuestion

      setBreadcrumbs(newBreadcrumbs)

      if (currentStep <= totalQuestions) {
        const newHistory = questionHistory.slice(0, -1)
        setQuestionHistory(newHistory)

        previousQuestion = newHistory[-1] ?? SOLUTION_FINDER_CONFIG
        setActiveQuestion(previousQuestion)
      }

      const newSolutionHistory = solutionHistory.slice(0, -1)
      setSolutionHistory(newSolutionHistory)

      const newSolution = newSolutionHistory.flat().map((item) => item)
      setSolution(newSolution)

      setTotalQuestions(calculateTotalQuestions(previousQuestion, previousStep))
      setMultiselectedAnswers([])
      setCurrentStep(previousStep)
    }
  }

  const handleRestart = () => {
    setCurrentStep(1)
    setTotalQuestions(calculateTotalQuestions(SOLUTION_FINDER_CONFIG, 1))
    setActiveQuestion(SOLUTION_FINDER_CONFIG)
    setQuestionHistory([SOLUTION_FINDER_CONFIG])
    setBreadcrumbs([])
    setSolution([])
    setSolutionHistory([])
    setMultiselectedAnswers([])
  }

  return (
    <Layout {...props}>
      {currentStep <= totalQuestions ? (
        <Question
          currentQuestionNumber={currentStep}
          totalQuestions={totalQuestions}
          question={activeQuestion}
          multiselectedAnswers={multiselectedAnswers}
          onAnswer={handleAnswer}
          onMultiselect={toggleMultiselectAnswer}
        />
      ) : (
        <Results
          solution={displaySolution}
          description={resultDescription}
          breadcrumbs={breadcrumbs}
          showLinks={!isInStoreVariant}
        />
      )}

      <SolutionNav
        progress={progress}
        currentStep={currentStep}
        totalQuestions={totalQuestions}
        isMultipleAnswers={activeQuestion.multipleAnswers ?? false}
        breadcrumbs={breadcrumbs}
        multiselectedAnswers={multiselectedAnswers}
        solution={displaySolution}
        showResultActions={!isInStoreVariant}
        onBack={handleBack}
        onRestart={handleRestart}
        onNext={() => handleAnswer(multiselectedAnswers)}
      />
    </Layout>
  )
}

export default SolutionFinder
